import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { SongCard } from '../components/SongCard';
import { GENRES } from '../data/mockData';
import type { Song } from '../types';

export function FeedPage() {
  const [wave, setWave] = useState<Song[]>([]);
  const [genre, setGenre] = useState('All');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [reportMsg, setReportMsg] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let live = true;
    api.wave().then((d) => { if (live) setWave(d.songs); }).catch((e:any) => setReportMsg(e.message));
    return () => { live = false; };
  }, []);

  const featured = wave.slice(0, 3);
  const filtered = useMemo(
    () => (genre === 'All' ? wave : wave.filter((s) => s.genre === genre)),
    [wave, genre],
  );

  const play = async (s: Song) => {
    if (playingId === s.id) { audioRef.current?.pause(); setPlayingId(null); return; }
    if (!audioRef.current) audioRef.current = new Audio();
    const url = api.audioUrl(s.fileUrl);
    if (!url) return;
    audioRef.current.src = url;
    audioRef.current.onended=()=>setPlayingId(null);
    try {await audioRef.current.play();setPlayingId(s.id);}catch{setReportMsg('This audio could not be played. Please try again.');return;}
    try { await api.play(s.id); } catch { /* ignore */ }
  };

  const report = async (s: Song) => {
    const reason = window.prompt('Why are you reporting this track?');
    if (!reason?.trim()) return;
    try {
      await api.reportSong(s.id, reason.trim(), '');
      setReportMsg('Report submitted. Thank you for helping keep IAMMUZIK safe.');
    } catch (e: any) {
      setReportMsg(e?.detail?.message || e?.message || 'Could not submit the report.');
    }
  };

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  return (
    <div className="grid">
      <div className="policy-banner" style={{ gridColumn: '1 / -1' }}>
        <strong> Parental Advisory on The Wave:</strong>{' '}
        The Wave requires a reviewed clean audio version. Private uploads stay in the owner’s library.
      </div>
      <div className="card">
        <div className="section-title" style={{ margin: 0 }}>Featured</div>
        <div className="muted mb-16" style={{ fontSize: 13 }}>Battle winners and promoted creators appear first.</div>
        {featured.length === 0 ? (
          <div className="empty">No featured tracks yet.</div>
        ) : (
          <div className="grid cols-3">
            {featured.map((s) => (
              <SongCard key={s.id} song={s}>
                <button className="btn btn-primary btn-sm" disabled={!s.fileUrl} onClick={() => play(s)}>{playingId === s.id ? '⏸ Pause' : '▶ Play'}</button>
                <button className="btn btn-outline btn-sm" onClick={() => report(s)}>⚠ Report</button>
              </SongCard>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex-between wrap gap-8">
          <div className="section-title" style={{ margin: 0 }}>Discover The Wave</div>
          <div className="flex gap-8 wrap">
            <button className={`btn btn-sm ${genre === 'All' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setGenre('All')}>All</button>
            {GENRES.map((g) => (
              <button key={g} className={`btn btn-sm ${genre === g ? 'btn-primary' : 'btn-outline'}`} onClick={() => setGenre(g)}>{g}</button>
            ))}
          </div>
        </div>

        <div className="divider" />

        {filtered.length === 0 ? (
          <div className="empty">No tracks in {genre} yet.</div>
        ) : (
          <div className="grid cols-4">
            {filtered.map((s) => (
              <SongCard key={s.id} song={s}>
                <button className="btn btn-primary btn-sm" disabled={!s.fileUrl} onClick={() => play(s)}>{playingId === s.id ? '⏸ Pause' : '▶ Play'}</button>
                <button className="btn btn-outline btn-sm" onClick={() => report(s)}>⚠ Report</button>
              </SongCard>
            ))}
          </div>
        )}

        <div className="field-hint mt-16">
          The Wave features both clean and explicit songs. Explicit tracks are tagged with a Parental
          Advisory badge and have profanity censored with [BEEP], like broadcast TV or radio.
          Uncensored versions are available in the artist's library (age-gated).
        </div>
      </div>
      {reportMsg && <div className="chip chip-approved">{reportMsg}</div>}
    </div>
  );
}
